import React from 'react';
import { Box, Container, Typography, Paper, Link } from '@mui/material';

const TermsPage: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Terms of Service
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: January 17, 2026
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            1. Acceptance of Terms
          </Typography>
          <Typography paragraph>
            By accessing and using Pasur Scorekeeper ("the Service"), you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use our Service.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            2. Use of Service
          </Typography>
          <Typography paragraph>
            Pasur Scorekeeper is provided for personal, non-commercial use. You agree to use the Service in compliance with all applicable laws and regulations. You must be at least 13 years old to use this Service.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            3. User Accounts
          </Typography>
          <Typography paragraph>
            You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You agree to:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <ul>
              <li>Provide accurate and complete information when creating your account</li>
              <li>Keep your login credentials secure and confidential</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            4. Subscriptions and Payments
          </Typography>
          <Typography paragraph>
            Pasur Scorekeeper offers optional premium subscriptions to remove advertisements. By subscribing, you agree to the following:
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Auto-Renewal:</strong> Subscriptions automatically renew at the end of each billing period (monthly or yearly) unless you cancel at least 24 hours before the renewal date.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Billing:</strong> Payment is charged to your Apple App Store or Google Play account at confirmation of purchase.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Price Changes:</strong> Subscription prices may change. We will notify you of any price changes before they take effect. Continued use after a price change constitutes acceptance of the new price.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Cancellation:</strong> You can cancel your subscription at any time through your App Store or Google Play account settings. Cancellation takes effect at the end of the current billing period.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            5. Refund Policy
          </Typography>
          <Typography paragraph>
            All purchases and subscriptions are processed through Apple App Store or Google Play Store. Refund requests must be submitted directly to the platform where you made the purchase:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <ul>
              <li><strong>Apple App Store:</strong> Request refunds through Apple's website or the Report a Problem page</li>
              <li><strong>Google Play Store:</strong> Request refunds through Google Play Help or the Google Play app</li>
            </ul>
          </Typography>
          <Typography paragraph sx={{ mt: 2 }}>
            We do not have direct access to your payment information and cannot process refunds directly. All refund decisions are made by Apple or Google according to their respective policies.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            6. User Conduct
          </Typography>
          <Typography paragraph>
            When using Pasur Scorekeeper, especially in multiplayer games, you agree not to:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <ul>
              <li>Harass, abuse, or threaten other players</li>
              <li>Use offensive or inappropriate display names or profile photos</li>
              <li>Cheat, exploit bugs, or use unauthorized third-party tools</li>
              <li>Intentionally disrupt games or abandon matches repeatedly</li>
              <li>Attempt to gain unauthorized access to other accounts or our systems</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            7. Intellectual Property
          </Typography>
          <Typography paragraph>
            All content, features, and functionality of Pasur Scorekeeper—including but not limited to text, graphics, logos, icons, images, audio, and software—are owned by us and are protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our written permission.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            8. Limitation of Liability
          </Typography>
          <Typography paragraph>
            To the maximum extent permitted by law, Pasur Scorekeeper and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <ul>
              <li>Your use of or inability to use the Service</li>
              <li>Any unauthorized access to your account or data</li>
              <li>Any interruption or cessation of the Service</li>
              <li>Any bugs, viruses, or other harmful code transmitted through the Service</li>
              <li>Any errors or omissions in content</li>
            </ul>
          </Typography>
          <Typography paragraph sx={{ mt: 2 }}>
            Our total liability for any claims arising from your use of the Service shall not exceed the amount you paid us in the 12 months prior to the claim.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            9. Dispute Resolution
          </Typography>
          <Typography paragraph>
            <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of the State of New York, United States, without regard to its conflict of law provisions.
          </Typography>
          <Typography paragraph>
            <strong>Informal Resolution:</strong> Before filing any formal dispute, you agree to first contact us at{' '}
            <Link href="mailto:team@pasurscorekeeper.com">
              team@pasurscorekeeper.com
            </Link>
            {' '}and attempt to resolve the dispute informally for at least 30 days.
          </Typography>
          <Typography paragraph>
            <strong>Jurisdiction:</strong> Any disputes that cannot be resolved informally shall be resolved exclusively in the state or federal courts located in New York.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            10. Account Termination
          </Typography>
          <Typography paragraph>
            <strong>By You:</strong> You may delete your account at any time through the Settings screen in the app. Upon deletion, your personal data will be removed as described in our Privacy Policy.
          </Typography>
          <Typography paragraph>
            <strong>By Us:</strong> We reserve the right to suspend or terminate your account if you violate these Terms, engage in prohibited conduct, or if we discontinue the Service. We will make reasonable efforts to notify you before termination unless immediate action is required.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            11. Service Availability
          </Typography>
          <Typography paragraph>
            We strive to keep Pasur Scorekeeper available at all times, but we do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control. We reserve the right to modify or discontinue the Service at any time.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            12. Changes to Terms
          </Typography>
          <Typography paragraph>
            We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the updated Terms in the app and updating the "Last Updated" date. Your continued use of the Service after changes are posted constitutes your acceptance of the modified Terms.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            13. Contact Information
          </Typography>
          <Typography paragraph>
            For any questions regarding these Terms of Service, please contact us at{' '}
            <Link href="mailto:team@pasurscorekeeper.com">
              team@pasurscorekeeper.com
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsPage;
